import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Card, Col, Container, Row, Table } from "react-bootstrap";
import RemoteConsoleInitial from "@/assets/images/remote-console-red.png";
import RemoteConsoleHovered from "@/assets/images/remote-console-white.png";
import * as devicesApi from "@/features/devices/devicesApi";
import {
  resetFilters,
  resetSecondaryFilters,
  setPrimaryFilters,
  setSecondaryFilters,
} from "@/features/filters/filtersSlice";
import { closeModal, setActiveModal } from "@/features/ui/uiSlice";
import BrandButton from "@/common/BrandButton/BrandButton";
import DateRangePicker from "@/common/DateRangePicker/DateRangePicker";
import FilterSelect from "@/common/FilterSelect/FilterSelect";
import PageTitle from "@/common/PageTitle/PageTitle";
import DeviceRow from "./DeviceRow";
import type { CheckboxProps } from "antd";
import { Checkbox, Form } from "antd";
import { FilterOutlined } from "@ant-design/icons";
import {
  Device,
  InitialFormValues,
  InputType,
  KeyMappings,
} from "@/features/devices/types";
import dayjs from "dayjs";
import FormItem from "@/common/FormItem/FormItem";
import TableRowNoResults from "@/common/TableRowNoResults/TableRowNoResults";
import PaginationRow from "@/components/PaginationRow/PaginationRow";
import DevicesControlPanelModal from "@/features/ui/modals/devices-control-panel/DevicesControlPanelModal";
import { ActiveModalNameEnum } from "@/features/ui/types";
import LoadingOverlay from "@/common/LoadingOverlay/LoadingOverlay";
import {
  DevicePreviewFixtures,
  isDevicePreviewAllowed,
  loadDevicePreviewFixtures,
} from "@/features/devices/devicePreview";
import * as devicePreview from "@/features/devices/devicePreview";
import styles from "./DevicesListStyles.module.scss";
const PAGE_SIZE = 20;
const keyMappings: KeyMappings = {
  master_accounts: "masterAccountId",
  zones: "zoneId",
  status: "status",
  playTimeStart: "playTimeStart",
  playTimeEnd: "playTimeEnd",
};
const initialFormValues: InitialFormValues = {
  master_accounts: null,
  zones: null,
  status: null,
  playTimeStart: null,
  playTimeEnd: null,
};
const selectInputs = [
  {
    label: "Master Accounts",
    name: "master_accounts",
    placeholder: "Any master account",
  },
  { label: "Zones", name: "zones", placeholder: "Any zone" },
  { label: "Status", name: "status", placeholder: "Any status" },
] as InputType[];
const tableHeaders = [
  {
    name: "Master Account",
    key: "master_account",
  },
  {
    name: "Location Name",
    key: "location_name",
  },
  {
    name: "Store #",
    key: "store_number",
  },
  {
    name: "Address",
    key: "address",
  },
  {
    name: "Zone Name",
    key: "zone",
  },
  {
    name: "Channel",
    key: "channel",
  },
  {
    name: "Last Played",
    key: "last_track_played_at",
  },
  {
    name: "Status",
    key: "status",
  },
  {
    name: "Actions",
    key: "actions",
  },
];
interface CheckedDevice {
  id: string;
  androidId: string;
}
const DevicesList: React.FC = () => {
  const primaryFilters = useSelector(
    (state: any) => state.filters.primaryFilters
  );
  const secondaryFilters = useSelector(
    (state: any) => state.filters.secondaryFilters
  );
  const [page, setPage] = useState<number>(1);
  const [checkedList, setChecked] = useState<CheckedDevice[]>([]);
  const [checkAll, setCheckAll] = useState<boolean>(false);
  const [isRemoteConsoleButtonHovered, setIsRemoteConsoleButtonHovered] =
    useState<boolean>(false);
  const [preview, setPreview] = useState<DevicePreviewFixtures | null>(null);
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const {
    data,
    isFetching: isFetchingDevices,
    isLoading: isLoadingDevices,
    error: devicesError,
  } = devicesApi.useGetDevicesQuery(
    { params: { status: "", ...primaryFilters }, page, pageSize: PAGE_SIZE },
    { skip: !devicePreview.isDeviceFleetListEnabled() }
  );
  const [saveLocationZone] = devicesApi.useSaveLocationZoneMutation();
  const handleSaveLocationZone = useCallback(
    (input: Parameters<typeof saveLocationZone>[0]) => saveLocationZone(input).unwrap(),
    [saveLocationZone]
  );
  
  // Debug logging
  React.useEffect(() => {
    console.log("🔍 Devices Debug - API Query State:");
    console.log("  - isLoading:", isLoadingDevices);
    console.log("  - isFetching:", isFetchingDevices);
    console.log("  - data:", data);
    console.log("  - error:", devicesError);
    console.log("  - primaryFilters:", primaryFilters);
  }, [isLoadingDevices, isFetchingDevices, data, devicesError, primaryFilters]);
  // Raw page exactly as returned by the Fleet API for this server page.
  // Pagination (disableNext) stays bound to this raw length so the display
  // filter below never affects server paging accuracy.
  const devices: Device[] = preview
    ? preview.DEVICE_PREVIEW_ROWS
    : data?.items || [];
  // Owner 2026-10-17: the Devices page permanently shows ASSIGNED devices
  // only. Warehouse / unassigned inventory lives on the Device Assignments
  // page; the Devices page has no warehouse/unassigned toggle.
  //
  // "Assigned" = the device has BOTH a location and a zone, identified by
  // canonical id OR display name. Canonical V2 Fleet rows can carry
  // location_name/zone_name without the *_id fields (see connected "Player 11"
  // mapping), so an id-only test would wrongly hide legitimately-assigned
  // devices. Warehouse / unassigned devices have neither a location nor a zone.
  const isAssignedDevice = (device: Device): boolean =>
    Boolean(
      (device.location_id || device.location_name) &&
        (device.zone_id || device.zone_name)
    );
  const visibleDevices: Device[] = devices.filter(isAssignedDevice);
  const handleOnTogglePreview = async () => {
    setChecked([]);
    setCheckAll(false);
    dispatch(closeModal());
    if (preview) {
      setPreview(null);
      return;
    }
    setPreview(await loadDevicePreviewFixtures());
  };
  const handleOnSelectOption = (_: string, option: any) => {
    if (!option?.type) return;
    const formFields = form.getFieldsValue(true);
    const { type: selectedOptionType } = option;
    const mappedFormValues = Object.keys(formFields).reduce((acc, key) => {
      const newKey = keyMappings[key];
      const value = formFields[key as keyof typeof formFields];
      if (newKey && value !== null && value !== undefined) {
        if (selectedOptionType === key && key === "master_accounts") {
          const { zoneId, ...newObject } = acc;
          acc = newObject;
          acc[newKey] = value;
          form.setFieldValue("zones", null);
          form.setFieldValue("messages", null);
          dispatch(setSecondaryFilters({ masterAccountId: value }));
        } else {
          acc[newKey] = value;
        }
      }
      return acc;
    }, {} as Record<string, string>);
    dispatch(setPrimaryFilters(mappedFormValues));
    setPage(1);
  };
  const handleDateChange = (dates: any) => {
    if (dates) {
      const [playTimeStart, playTimeEnd] = dates;
      form.setFieldsValue({
        playTimeStart: playTimeStart.utc().format("YYYY-MM-DD"),
        playTimeEnd: playTimeEnd.utc().format("YYYY-MM-DD"),
      });
    } else {
      form.setFieldsValue({
        playTimeStart: null,
        playTimeEnd: null,
      });
    }
    const formFields = form.getFieldsValue(true);
    const updatedPrimaryFilters = Object.keys(formFields).reduce((acc, key) => {
      const newKey = keyMappings[key];
      const value = formFields[key as keyof typeof formFields];
      if (newKey && value !== null && value !== undefined) {
        acc[newKey] = value;
      }
      return acc;
    }, {} as Record<string, string>);
    dispatch(setPrimaryFilters(updatedPrimaryFilters));
    setPage(1);
  };
  const handleOnClearInput = (input: InputType) => {
    const { name } = input;
    const filterId = keyMappings[name];
    let { ...updatedPrimaryFilters } = primaryFilters;
    updatedPrimaryFilters = Object.keys(updatedPrimaryFilters).reduce(
      (acc: any, key: any) => {
        const keysToRemove: string[] = [filterId];
        switch (filterId) {
          case "masterAccountId":
            keysToRemove.push("zoneId", "messageId");
            form.setFieldValue("zones", null);
            dispatch(resetSecondaryFilters());
            break;
          default:
            break;
        }
        if (!keysToRemove.includes(key)) {
          acc[key] = primaryFilters[key];
        }
        return acc;
      },
      {} as Record<string, string>
    );
    dispatch(setPrimaryFilters(updatedPrimaryFilters));
    setPage(1);
  };
  const handleResetFilters = () => {
    form.resetFields();
    setCheckAll(false);
    setChecked([]);
    dispatch(resetFilters());
    setPage(1);
  };
  const handleOnClickNext = async () => {
    setPage((currentPage) => currentPage + 1);
  };
  const handleOnClickPrev = async () => {
    setPage((currentPage) => Math.max(currentPage - 1, 1));
  };
  const handleOnCheckboxChange = (event: any, device: Device) => {
    const checked = event.target.checked;
    const checkedDevice: CheckedDevice = {
      id: device.id,
      androidId: device.android_id,
    };
    setChecked((prev) => {
      if (checked) {
        return [...prev, checkedDevice];
      } else {
        return prev.filter((item) => item.id !== checkedDevice.id);
      }
    });
  };
  const handleOnCheckAllChange: CheckboxProps["onChange"] = (e) => {
    // Only ever select devices that are actually visible in the current view.
    const allDeviceIds = visibleDevices.map((device) => ({
      id: device.id,
      androidId: device.android_id,
    }));
    setChecked(e.target.checked ? allDeviceIds : []);
    setCheckAll((prev) => !prev);
  };
  const handleOnUpdateDevices = () => {
    // Remote Console must receive string deviceIds only (not CheckedDevice objects).
    dispatch(
      setActiveModal({
        name: ActiveModalNameEnum.Device,
        ids: checkedList.map((d) => d.id),
      })
    );
  };
  const encodeAddressUri = (address: string) => {
    const encodedAddress = encodeURIComponent(address);
    const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
    window.open(mapsUrl, "_blank");
  };
  const handleOnHideModal = useCallback(() => {
    dispatch(closeModal());
  }, [dispatch]);
  useEffect(() => {
    return () => {
      dispatch(closeModal());
    };
  }, [dispatch]);
  const handleOnModalSubmitSuccess = () => {
    setCheckAll(false);
    setChecked([]);
  };
  const handleOnEnterRemoteConsoleButton = () =>
    setIsRemoteConsoleButtonHovered(true);
  const handleOnLeaveRemoteConsoleButton = () =>
    setIsRemoteConsoleButtonHovered(false);
  return (
    <Container fluid>
      <Form
        requiredMark={false}
        layout="vertical"
        initialValues={initialFormValues}
        form={form}
      >
        <Card>
          <Card.Body>
            <Card.Title style={{ display: 'flex', justifyContent: 'space-between' }}>
              <PageTitle title="Devices" />
              <BrandButton
                variant="outline"
                disabled={!primaryFilters}
                onClick={handleResetFilters}
              >
                <FilterOutlined /> Reset All
              </BrandButton>
            </Card.Title>
            {(isLoadingDevices || isFetchingDevices) && <LoadingOverlay />}
            {devicePreview.isDeviceFleetListEnabled() && devicesError && (
              <div className="alert alert-danger" role="alert">
                <strong>API Error:</strong> {JSON.stringify(devicesError)}
              </div>
            )}
            {!devicePreview.isDeviceFleetListEnabled() && !preview && (
              <div
                className="alert alert-secondary d-flex justify-content-between align-items-center"
                role="status"
                data-testid="devices-registry-disconnected"
              >
                <div>
                  <strong>Device registry not connected yet</strong>
                  <div>
                    KrossKast Device Fleet V2 is not connected to this page yet.
                  </div>
                </div>
                {isDevicePreviewAllowed() && (
                  <BrandButton
                    variant="outline"
                    type="button"
                    onClick={handleOnTogglePreview}
                  >
                    Preview Existing Device UI
                  </BrandButton>
                )}
              </div>
            )}
            {preview && (
              <div
                className="alert alert-warning d-flex justify-content-between align-items-center"
                role="status"
                data-testid="devices-preview-banner"
              >
                <strong>{preview.DEVICE_PREVIEW_LABEL}</strong>
                <BrandButton
                  variant="outline"
                  type="button"
                  onClick={handleOnTogglePreview}
                >
                  Exit Preview
                </BrandButton>
              </div>
            )}
            <Row className="my-2">
              <Col>
                <Row>
                  {selectInputs.map((input) => (
                    <Col key={input.name} md={4}>
                      <FormItem
                        name={input.name}
                        label={input.label}
                        className="customSelectWrapper"
                      >
                        <FilterSelect<"devices">
                          onSelect={handleOnSelectOption}
                          name={input.name}
                          extraData={
                            input.name === "status"
                              ? [
                                  {
                                    value: "good",
                                    label: "Good",
                                    type: "status",
                                  },
                                  {
                                    value: "check",
                                    label: "Check",
                                    type: "status",
                                  },
                                ]
                              : []
                          }
                          placeholder={input.placeholder}
                          filterParams={secondaryFilters}
                          remoteOptionsDisabled={!devicePreview.isDeviceFleetListEnabled()}
                          onClear={() => handleOnClearInput(input)}
                        />
                      </FormItem>
                    </Col>
                  ))}
                </Row>
              </Col>
            </Row>
            <Row className="my-2">
              <Col
                sm={12}
                lg={6}
                className="d-flex flex-column justify-content-end"
              >
                <FormItem
                  name="dateRange"
                  validateTrigger="onFinish"
                  label="Filter by Date Range"
                  className="mb-0"
                >
                  <DateRangePicker
                    name="dateRange"
                    maxDate={dayjs()}
                    onChange={handleDateChange}
                  />
                </FormItem>
              </Col>
              <Col
                lg={{ span: 4, offset: 2 }}
                className="d-flex align-items-end mt-3 mt-lg-0"
              >
                <BrandButton
                  className="d-flex align-items-center justify-content-center outline w-100"
                  disabled={!checkedList.length}
                  name="deviceUpdate"
                  onClick={handleOnUpdateDevices}
                  type="button"
                  onMouseEnter={handleOnEnterRemoteConsoleButton}
                  onMouseLeave={handleOnLeaveRemoteConsoleButton}
                  variant="outline-primary"
                >
                  <img
                    style={{
                      width: 30,
                      height: 30,
                      objectFit: "contain",
                      marginRight: 10,
                    }}
                    src={
                      isRemoteConsoleButtonHovered
                        ? RemoteConsoleHovered
                        : RemoteConsoleInitial
                    }
                    alt="Remote Console"
                  />
                  <span>Remote Console</span>
                </BrandButton>
              </Col>
            </Row>
            <Row className="my-2">
              <Col>
                <span className="text-muted" data-testid="devices-assigned-count">
                  Showing {visibleDevices.length}{" "}
                  assigned device{visibleDevices.length === 1 ? "" : "s"}
                </span>
              </Col>
            </Row>
            <Row className="mt-4">
              <Col>
                <Table hover responsive className={`legacy-row-height-table devices-table`}>
                  {/*
                   * Fixed column model (owner 2026-10-17). With
                   * `table-layout: fixed` (legacyTables.scss) these widths —
                   * not cell content — determine column geometry, so entering
                   * inline edit (text → inputs) cannot resize columns, re-wrap
                   * headers, or change header/row heights. Order matches the
                   * header/body columns exactly: checkbox, then tableHeaders.
                   */}
                  <colgroup>
                    <col style={{ width: "4%" }} />
                    <col style={{ width: "12%" }} />{/* Master Account */}
                    <col style={{ width: "13%" }} />{/* Location Name */}
                    <col style={{ width: "6%" }} />{/* Store # */}
                    <col style={{ width: "17%" }} />{/* Address */}
                    <col style={{ width: "11%" }} />{/* Zone Name */}
                    <col style={{ width: "13%" }} />{/* Channel */}
                    <col style={{ width: "8%" }} />{/* Last Played */}
                    <col style={{ width: "7%" }} />{/* Status */}
                    <col style={{ width: "9%" }} />{/* Actions */}
                  </colgroup>
                  <thead>
                    <tr>
                      <th>
                        <Checkbox
                          checked={checkAll}
                          style={{ color: "#ffffff" }}
                          onChange={handleOnCheckAllChange}
                        />
                      </th>
                      {tableHeaders.map((header, index) => {
                        if (header.key === "location_name") {
                          return (
                            <th key={index} style={{ whiteSpace: 'nowrap', maxWidth: 160 }}>
                              {header.name}
                            </th>
                          );
                        }
                        if (header.key === "last_track_played_at") {
                          return (
                            <th key={index} style={{ whiteSpace: 'nowrap', maxWidth: 120 }}>
                              {header.name}
                            </th>
                          );
                        }
                        if (header.key === "address") {
                          return (
                            <th key={index} style={{ whiteSpace: 'normal', maxWidth: 120 }}>
                              {header.name}
                            </th>
                          );
                        }
                        if (header.key === "status") {
                          return (
                            <th key={index} style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                              {header.name}
                            </th>
                          );
                        }
                        // Match DeviceRow actionCell width so Save/Cancel never reflows the table.
                        if (header.key === "actions" || header.name === "Actions") {
                          return (
                            <th
                              key={index}
                              style={{
                                width: 118,
                                minWidth: 118,
                                maxWidth: 118,
                                textAlign: "center",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {header.name}
                            </th>
                          );
                        }
                        return <th key={index}>{header.name}</th>;
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {visibleDevices.map((device: Device, index: number) => (
                      <DeviceRow
                        key={index}
                        {...device}
                        checked={checkedList.some(
                          (item) => item.id === device.id
                        )}
                        onChangeCheckbox={handleOnCheckboxChange}
                        onClickAddress={encodeAddressUri}
                        onSaveLocationZone={handleSaveLocationZone}
                      />
                    ))}
                    {visibleDevices.length === 0 && (
                      <TableRowNoResults colSpan={tableHeaders.length + 1} />
                    )}
                  </tbody>
                </Table>
              </Col>
            </Row>
            <PaginationRow
              onClickPrevious={handleOnClickPrev}
              handleOnClickNext={handleOnClickNext}
              disableLeft={page === 1 || isFetchingDevices || isLoadingDevices}
              disableNext={
                isFetchingDevices || isLoadingDevices || devices.length < PAGE_SIZE
              }
              page={page}
              onPageChange={(p) => setPage(p)}
            />
          </Card.Body>
        </Card>
      </Form>
      <DevicesControlPanelModal
        onSuccess={handleOnModalSubmitSuccess}
        onHide={handleOnHideModal}
        commandsEnabled={
          devicePreview.isDeviceFleetCommandsEnabled() && !preview
        }
        commandsDisabledNotice={
          preview?.DEVICE_PREVIEW_COMMANDS_NOTICE ||
          (!devicePreview.isDeviceFleetCommandsEnabled()
            ? devicePreview.PRODUCTION_COMMANDS_DISABLED_NOTICE
            : undefined)
        }
      />
    </Container>
  );
};
export default DevicesList;